import { useEffect } from 'react';

import { WIZARD_STEP } from '../SourceCreate.constants';
import {
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';
import type {
  UseFormResetParams,
  UseFormResetReturn
} from './SourceCreateDialog.types';

/**
 * Custom hook to manage complete form reset logic.
 *
 * This complex reset is required to prevent issues with dynamic form fields:
 * - React Hook Form persists field registrations across source type changes
 * - Dynamic fields from old schemas can interfere with new schemas
 * - Stale values can cause validation errors or incorrect submissions
 *
 * The multi-step process ensures clean state transitions.
 *
 * @param params - Form methods and state setters
 * @returns resetAll function
 */
export const useFormReset = ({
  mode,
  currentStep,
  showSourcePopup,
  getValues,
  reset,
  unregister,
  selectedOrgId,
  setError,
  setCategories,
  setRegions,
  setCloudProviders,
  setDeviceAlertFields,
  setVulnFields,
  setSelectedSourceTypeId,
  setWizardSelectedSource,
  setConnectionInputSchema
}: UseFormResetParams): UseFormResetReturn => {
  /**
   * Resets all form state to initial values.
   *
   * The multi-step process ensures clean state transitions:
   */
  const resetAll = () => {
    // STEP 1: Capture essential values before reset
    const deviceUid = getValues('device_uid');
    const orgId = selectedOrgId;

    // STEP 2: Clear the schema to prevent old fields from rendering
    // This prevents flicker and ensures dynamic fields are truly removed
    setConnectionInputSchema({});

    // STEP 3: Define core fields that should be preserved
    // These fields maintain form continuity across source type changes
    const coreFields = [
      'source_type_id',
      'device_uid',
      'org_id',
      'organization_name',
      'device_name',
      'ip_address'
    ];

    // STEP 4: Unregister all old dynamic fields
    // Dynamic fields are source-type-specific and must be cleared to prevent stale data
    const allFields = Object.keys(getValues());
    const fieldsToUnregister = allFields.filter(
      (field) => !coreFields.includes(field)
    );

    if (fieldsToUnregister.length > 0) {
      fieldsToUnregister.forEach((field) => {
        unregister(field as keyof SourceCreateDynamicFormModel, {
          keepValue: false,
          keepError: false,
          keepDirty: false,
          keepTouched: false,
          keepIsValid: false,
          keepDefaultValue: false
        });
      });
    }

    // STEP 5: Completely reset form to ONLY core values
    reset(
      {
        device_uid: deviceUid,
        org_id: orgId
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
        keepDirtyValues: false,
        keepValues: false,
        keepTouched: false,
        keepIsSubmitted: false,
        keepSubmitCount: false
      }
    );

    // STEP 6: Clear all related state variables
    setError('');
    setCategories([]);
    setRegions([]);
    setCloudProviders([]);
    setDeviceAlertFields([]);
    setVulnFields([]);

    // STEP 7: Reset source type ID and wizard selection
    setSelectedSourceTypeId(0);
    setWizardSelectedSource(null);
  };

  // Reset form when Step 1 loads in New mode
  useEffect(() => {
    if (
      mode === SourceDialogMode.New &&
      currentStep === WIZARD_STEP.SELECT_SOURCE &&
      showSourcePopup
    ) {
      resetAll();
    }
  }, [mode, currentStep, showSourcePopup]);

  return { resetAll };
};
